import { useState } from 'react';
import {
  Chip, IconButton, Tooltip, Stack, TextField, MenuItem, Menu, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Divider, Box, Alert, Snackbar,
} from '@mui/material';
import { Visibility, MoreVert, Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable, type Column } from '../../../components/DataTable';
import { clientOnboardingApi, type ClientApplication, type ClientReviewDecision } from '../../../api/clientOnboarding.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';
import { PortalUsersDialog } from '../../../components/PortalUsersDialog';
import { ClientVerticalsDialog } from '../../../components/ClientVerticalsDialog';

const STAGE_DECISIONS: ClientReviewDecision[] = ['approve', 'reject', 'send_back', 'request_clarification'];
const DECISION_LABEL: Record<ClientReviewDecision, string> = { approve: 'Approve', reject: 'Reject', send_back: 'Send Back for Correction', request_clarification: 'Request Clarification' };
const DECISION_COLOR: Record<ClientReviewDecision, 'success' | 'error' | 'warning' | 'info'> = { approve: 'success', reject: 'error', send_back: 'warning', request_clarification: 'info' };
const statusColor = (s: string): 'default' | 'success' | 'error' | 'warning' | 'info' =>
  ['approved', 'active'].includes(s) ? 'success' : s === 'rejected' ? 'error' : s === 'inactive' ? 'default'
    : ['clarification_required', 'sent_back'].includes(s) ? 'warning' : 'info';
const human = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '—');
const PENDING = ['submitted', 'under_review', 'commercial_review', 'clarification_required'];

export function ClientApplicationsPage() {
  const { hasPermission, user } = useAuth();
  const isAdmin = (user?.roles ?? []).some((r) => r === 'super_admin' || r === 'admin');
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('');
  const [reviewId, setReviewId] = useState<number | null>(null);
  const [usersClient, setUsersClient] = useState<{ id: number; name: string } | null>(null);
  const [verticalsClient, setVerticalsClient] = useState<{ id: number; name: string } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const canCreate = hasPermission('client_onboarding.create');
  const canReview = hasPermission('client_onboarding.review');
  const canSubmit = hasPermission('client_onboarding.submit');
  const canStatus = hasPermission('client_onboarding.change_status');

  const { data, isLoading } = useQuery({ queryKey: ['client-applications', page, limit, status], queryFn: () => clientOnboardingApi.list({ page, limit, status: status || undefined }) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['client-applications'] });
  const run = (p: Promise<unknown>, msg: string) => p.then(() => { setToast(msg); refresh(); }).catch((e) => setToast(extractApiError(e)));

  const newApp = () => clientOnboardingApi.create({ company_name: 'New Client' }).then((a) => { refresh(); navigate(`/client-applications/${a.id}/edit`); }).catch((e) => setToast(extractApiError(e)));

  const columns: Column<ClientApplication>[] = [
    { key: 'application_code', label: 'Code', width: 140 },
    { key: 'company_name', label: 'Company' },
    { key: 'contact_person', label: 'Contact', render: (r) => (r.contact_person ? <Box><Typography variant="body2">{r.contact_person}</Typography><Typography variant="caption" color="text.secondary">{r.contact_mobile ?? r.contact_email ?? ''}</Typography></Box> : '—') },
    { key: 'billing_structure', label: 'Billing', render: (r) => human(r.billing_structure) },
    { key: 'source_type', label: 'Source', render: (r) => r.source_type ?? '—' },
    { key: 'current_stage', label: 'Stage', render: (r) => human(r.current_stage) },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" color={statusColor(r.status)} label={human(r.status)} /> },
    { key: 'submitted_at', label: 'Submitted', render: (r) => (r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '—') },
  ];

  return (
    <>
      <PageHeader title="Client Applications" subtitle="Client onboarding — Draft → Under Review → Commercial Review → Approved → Active."
        action={canCreate && <Button startIcon={<Add />} variant="contained" onClick={newApp}>New Application</Button>} />

      <Stack direction="row" mb={2}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 240 }}>
          <MenuItem value="">All</MenuItem>
          {['draft', 'sent_back', 'under_review', 'commercial_review', 'clarification_required', 'approved', 'active', 'inactive', 'rejected'].map((s) => <MenuItem key={s} value={s}>{human(s)}</MenuItem>)}
        </TextField>
      </Stack>

      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <RowActions row={r} perms={{ canReview, canSubmit, canStatus, canCreate, isAdmin }}
            onView={() => setReviewId(r.id)}
            onEdit={() => navigate(`/client-applications/${r.id}/edit`)}
            onUsers={() => r.client_id && setUsersClient({ id: r.client_id, name: r.company_name })}
            onVerticals={() => r.client_id && setVerticalsClient({ id: r.client_id, name: r.company_name })}
            onSubmit={() => run(clientOnboardingApi.submit(r.id), 'Submitted for review')}
            onWithdraw={() => run(clientOnboardingApi.withdraw(r.id), 'Application withdrawn')}
            onDelete={() => run(clientOnboardingApi.remove(r.id), 'Draft deleted')}
            onReopen={() => run(clientOnboardingApi.reopen(r.id), 'Application reopened')}
            onClone={() => clientOnboardingApi.clone(r.id).then((a) => { setToast('Cloned to a new draft'); refresh(); navigate(`/client-applications/${a.id}/edit`); }).catch((e) => setToast(extractApiError(e)))}
            onActivate={() => run(clientOnboardingApi.changeStatus(r.id, 'active'), 'Client activated')}
            onDeactivate={() => run(clientOnboardingApi.changeStatus(r.id, 'inactive'), 'Client deactivated')}
            onAdminDelete={() => { if (window.confirm(`Delete client "${r.company_name}" and its portal login? This soft-deletes the client master + login. Continue?`)) run(clientOnboardingApi.adminDelete(r.id), 'Client + login deleted'); }}
          />
        )} />

      {reviewId && <ReviewDialog id={reviewId} canReview={canReview} onClose={() => setReviewId(null)} />}
      <PortalUsersDialog open={Boolean(usersClient)} resource="clients" entityId={usersClient?.id ?? null} entityName={usersClient?.name} onClose={() => setUsersClient(null)} />
      <ClientVerticalsDialog open={Boolean(verticalsClient)} clientId={verticalsClient?.id ?? null} clientName={verticalsClient?.name} onClose={() => setVerticalsClient(null)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}

interface Perms { canReview: boolean; canSubmit: boolean; canStatus: boolean; canCreate: boolean; isAdmin: boolean }

function RowActions({ row, perms, onView, onEdit, onUsers, onVerticals, onSubmit, onWithdraw, onDelete, onReopen, onClone, onActivate, onDeactivate, onAdminDelete }: {
  row: ClientApplication; perms: Perms;
  onView: () => void; onEdit: () => void; onUsers: () => void; onVerticals: () => void; onSubmit: () => void; onWithdraw: () => void; onDelete: () => void;
  onReopen: () => void; onClone: () => void; onActivate: () => void; onDeactivate: () => void; onAdminDelete: () => void;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const close = () => setAnchor(null);
  const s = row.status;
  const isDraft = s === 'draft' || s === 'sent_back' || s === 'clarification_required';
  const isPending = PENDING.includes(s);
  const items: Array<{ label: string; onClick: () => void; color?: string }> = [];
  if (isDraft && perms.canSubmit) { items.push({ label: 'Edit', onClick: onEdit }); if (s !== 'clarification_required') items.push({ label: 'Submit', onClick: onSubmit }); items.push({ label: 'Delete', onClick: onDelete, color: 'error' }); }
  // Admin / Super Admin may edit at any stage (incl. an approved/active client).
  if (!isDraft && perms.isAdmin) items.push({ label: 'Edit', onClick: onEdit });
  if (isPending) { if (perms.canReview) items.push({ label: 'Review', onClick: onView }); if (perms.canSubmit) items.push({ label: 'Withdraw', onClick: onWithdraw, color: 'warning' }); }
  if (s === 'active' && perms.canStatus) items.push({ label: 'Deactivate', onClick: onDeactivate, color: 'warning' });
  if (s === 'inactive' && perms.canStatus) items.push({ label: 'Activate', onClick: onActivate });
  if (s === 'rejected') { if (perms.canReview) items.push({ label: 'Reopen', onClick: onReopen }); if (perms.canCreate) items.push({ label: 'Clone', onClick: onClone }); }
  // Team / portal users + service mapping — once the client is activated (has a client_id).
  if (row.client_id && (perms.isAdmin || perms.canStatus)) {
    items.push({ label: 'Users / Team', onClick: onUsers });
    items.push({ label: 'Services / Verticals', onClick: onVerticals });
  }
  // Admin / Super Admin: delete the client master + its portal login outright.
  if (perms.isAdmin && row.client_id) items.push({ label: 'Delete client + login', onClick: onAdminDelete, color: 'error' });

  return (
    <>
      <Tooltip title="View"><IconButton size="small" onClick={onView}><Visibility fontSize="small" /></IconButton></Tooltip>
      {items.length > 0 && (
        <>
          <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}><MoreVert fontSize="small" /></IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
            {items.map((it) => <MenuItem key={it.label} onClick={() => { close(); it.onClick(); }} sx={it.color ? { color: `${it.color}.main` } : undefined}>{it.label}</MenuItem>)}
          </Menu>
        </>
      )}
    </>
  );
}

function ReviewDialog({ id, canReview, onClose }: { id: number; canReview: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data: app } = useQuery({ queryKey: ['client-application', id], queryFn: () => clientOnboardingApi.get(id) });

  const reviewMut = useMutation({
    mutationFn: (decision: ClientReviewDecision) => clientOnboardingApi.review(id, decision, remarks || undefined),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['client-applications'] }); qc.invalidateQueries({ queryKey: ['client-application', id] }); setRemarks(''); setError(null); },
    onError: (e) => setError(extractApiError(e)),
  });

  const isPending = app ? PENDING.includes(app.status) : false;
  const terminal = app ? ['approved', 'active', 'rejected'].includes(app.status) : false;

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>{app ? `${app.application_code} — ${app.company_name}` : 'Loading…'}</DialogTitle>
      <DialogContent dividers>
        {app && (
          <Stack spacing={2}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Field label="GST" value={app.gst_number} />
              <Field label="PAN" value={app.pan_number} />
              <Field label="Billing" value={human(app.billing_structure)} />
              <Field label="Credit (days)" value={app.credit_period_days != null ? String(app.credit_period_days) : null} />
              <Field label="Contact" value={app.contact_person ? `${app.contact_person} · ${app.contact_mobile ?? app.contact_email ?? ''}` : null} />
              <Box><Typography variant="caption" color="text.secondary">Status</Typography><Box><Chip size="small" color={statusColor(app.status)} label={human(app.status)} /></Box></Box>
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Approval workflow</Typography>
            <Stack spacing={0.5}>
              {(app.workflow ?? []).map((w) => (
                <Stack key={w.id} direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={`${w.stage_order}. ${human(w.stage)}`} variant="outlined" />
                  <Chip size="small" color={statusColor(w.status)} label={human(w.status)} />
                  {w.remarks && <Typography variant="caption" color="text.secondary">— {w.remarks}</Typography>}
                </Stack>
              ))}
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Internal review (admin only)</Typography>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Field label="General Review" value={app.general_review_status} />
              <Field label="Commercial Review" value={app.commercial_review_status} />
            </Stack>

            {canReview && !terminal && isPending && (
              <>
                <Divider />
                <Typography variant="subtitle2">Action — {human(app.current_stage)} stage</Typography>
                <TextField size="small" label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} multiline minRows={2} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {STAGE_DECISIONS.map((d) => (
                    <Button key={d} variant="contained" color={DECISION_COLOR[d]} disabled={reviewMut.isPending} onClick={() => reviewMut.mutate(d)}>{DECISION_LABEL[d]}</Button>
                  ))}
                </Stack>
              </>
            )}
            {terminal && <Alert severity={app.status === 'rejected' ? 'error' : 'success'}>This application is {human(app.status)}{app.client_id ? ` — client master #${app.client_id} created.` : '.'}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return <Box><Typography variant="caption" color="text.secondary">{label}</Typography><Typography>{value ?? '—'}</Typography></Box>;
}
